import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  MessageCircleMore,
  X,
  ImagePlus,
  Trash2,
  ArrowLeft,
  Phone,
  MessageSquare,
  Mail,
  MapPin,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { io } from "socket.io-client";
import globalVariable from "../GlobalVariable";
import api from "utils/api";
import ReactMarkdown from "react-markdown";
import { useAuth } from "../context/AuthContext";
import { jwtDecode } from "jwt-decode";
import FormAzucarai from "modules/client/form_azucarai";

const ROOM_ID_KEY = "bluechatwidget_room_id";

const BlueChatWidget = ({ isEmbedded = false }) => {
  const [isWindowOpen, setWindowOpen] = useState(false);
  const [isBarVisible, setIsBarVisible] = useState(false);
  const [isCompanyInfoVisible, setCompanyInfoVisible] = useState(false);
  const [activeView, setActiveView] = useState('chat'); // 'chat' or 'form'
  const [formTitle, setFormTitle] = useState('Message Us');
  const [inputValue, setInputValue] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [request_contact_is_send, set_request_contact_is_send] =
    useState(false);
  const [roomId, setRoomId] = useState(() => {
    try {
      const stored = localStorage.getItem(ROOM_ID_KEY);
      return (
        stored ||
        `room_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`
      );
    } catch (e) {
      return `room_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    }
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsBarVisible(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      if (messages[messages.length - 1].role === "assistant") {
        setIsStreaming(false);
      }
    }
  }, [messages]);

  // Estado para rastrear la visibilidad de la página
  const [isPageVisible, setIsPageVisible] = useState(() => !document.hidden);

  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const socketRef = useRef(null);

  const [pendingImage, setPendingImage] = useState(null);
  const { user } = useAuth();
  const [showQuickOptions, setShowQuickOptions] = useState(true);
  const [lastSelectionWasService, setLastSelectionWasService] = useState(false);

  const handleCloseWindow = useCallback(() => {
    setWindowOpen(false);
    setCompanyInfoVisible(false);
  }, []);

  useEffect(() => {
    if (request_contact_is_send) {
      handleCloseWindow();
      set_request_contact_is_send(false);
    }
  }, [request_contact_is_send, handleCloseWindow]);

  const formatBytes = (bytes) => {
    if (!bytes) return "0 B";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);
    return `${value.toFixed(i === 0 ? 0 : 2)} ${sizes[i]}`;
  };

  const socketUrl = useMemo(() => {
    return globalVariable.domain;
  }, []);

  // Obtener user_id del usuario autenticado o generar invitado
  const getUserId = useCallback(() => {
    // 1) Si el contexto ya tiene usuario decodificado
    if (user && user._id) {
      return user._id;
    }

    // 2) Si existe token, intentar decodificarlo por si el contexto aún no está listo
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded && decoded._id) {
          return decoded._id;
        }
      } catch (e) {
        // ignore decode errors
      }
    }

    // 3) Usuario invitado
    let guestId = sessionStorage.getItem("guest_user_id");
    if (!guestId) {
      guestId = `guest_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .substring(2, 8)}`;
      sessionStorage.setItem("guest_user_id", guestId);
    }
    return guestId;
  }, [user]);

  // Guardar room_id en localStorage
  useEffect(() => {
    try {
      localStorage.setItem(ROOM_ID_KEY, roomId);
    } catch (e) {
      // ignore
    }
  }, [roomId]);

  // Configurar Socket.IO
  useEffect(() => {
    if (!socketRef.current) {
      console.log("🔌 Intentando conectar Socket.IO a:", socketUrl);
      
      socketRef.current = io(socketUrl, {
        transports: ["polling", "websocket"], // Intentar polling primero
        timeout: 20000,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        forceNew: true,
      });

      socketRef.current.on("connect", () => {
        console.log("✅ Socket.IO conectado exitosamente");
        setIsConnected(true);
        socketRef.current.emit("join_room", roomId);
      });

      socketRef.current.on("disconnect", (reason) => {
        console.log("⚠️ Socket.IO desconectado. Razón:", reason);
        setIsConnected(false);
      });

      socketRef.current.on("new_message", (messageData) => {
        setMessages((prev) => {
          const existsById = prev.some(
            (msg) => msg._id === messageData._id && !msg.isLocal
          );
          if (existsById) {
            return prev;
          }
          if (!messageData.is_chat_bot) {
            const existsLocal = prev.some(
              (msg) =>
                msg.role === "user" &&
                msg.content === messageData.message &&
                msg.isLocal &&
                Math.abs(
                  new Date(msg.createdAt).getTime() -
                  new Date(messageData.createdAt).getTime()
                ) < 5000
            );
            if (existsLocal) {
              return prev.map((msg) =>
                msg.role === "user" &&
                  msg.content === messageData.message &&
                  msg.isLocal
                  ? {
                    role: "user",
                    content: messageData.message,
                    _id: messageData._id,
                    createdAt: messageData.createdAt,
                    isLocal: false,
                  }
                  : msg
              );
            }
          }
          return [
            ...prev,
            {
              role: messageData.is_chat_bot ? "assistant" : "user",
              is_chat_ai: true,
              content: messageData.message,
              _id: messageData._id,
              createdAt: messageData.createdAt,
              isLocal: false,
            },
          ];
        });
        if (messageData.is_chat_bot) {
          if (lastSelectionWasService) {
            setShowQuickOptions(true);
            setLastSelectionWasService(false);
          } else {
            setShowQuickOptions(true);
          }
        }
      });

      socketRef.current.on("connect_error", (error) => {
        console.error("❌ Error de conexión Socket.IO:", error);
        console.error("❌ Tipo de error:", error.type);
        console.error("❌ Mensaje:", error.message);
        console.error("❌ Descripción:", error.description);
        setIsConnected(false);
      });

      socketRef.current.on("reconnect_attempt", (attempt) => {
        console.log(`🔄 Intento de reconexión #${attempt}`);
      });

      socketRef.current.on("reconnect_failed", () => {
        console.error("❌ Falló la reconexión después de todos los intentos");
      });
    }

    return () => {
      if (socketRef.current) {
        console.log("🔌 Desconectando Socket.IO...");
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [socketUrl, roomId, lastSelectionWasService]);

  const loadExistingMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { "Content-Type": "application/json" };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const response = await api.get(`/chat/room/${roomId}`, { headers });
      if (response.data.success) {
        const data = response.data.content;
        if (data.success && Array.isArray(data.content)) {
          const formattedMessages = data.content.map((msg) => ({
            role: msg.is_chat_bot ? "assistant" : "user",
            is_chat_ai: true,
            content: msg.message,
            _id: msg._id,
            createdAt: msg.createdAt,
            isLocal: false,
          }));
          setMessages(formattedMessages);
        }
      }
    } catch (error) {
      console.error("Error cargando mensajes:", error);
    }
  }, [roomId]);

  useEffect(() => {
    // Nos aseguramos de que el widget se está ejecutando dentro de un iframe
    if (window.parent !== window) {
      const message = {
        type: 'azucar-chat-resize',
        // Envía 'open' cuando la ventana del chat se abre, y 'closed' cuando se cierra.
        status: isWindowOpen ? 'open' : 'closed'
      };
      // Envía el mensaje a la página que contiene el iframe.
      // Por seguridad, en producción deberías reemplazar '*' con el dominio de tu sitio de WordPress.
      window.parent.postMessage(message, '*');
    }
  }, [isWindowOpen]); // Este efecto se ejecuta cada vez que el estado 'isWindowOpen' cambia.


  // Cargar mensajes existentes cuando se abre el chat
  useEffect(() => {
    if (isWindowOpen && activeView === 'chat' && isConnected && messages.length === 0) {
      loadExistingMessages();
    }
  }, [isWindowOpen, activeView, isConnected, messages.length, loadExistingMessages]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, isWindowOpen, isStreaming]);

  useEffect(() => {
    const handleVisibilityChange = () => setIsPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (messages.length === 0 || isStreaming) return;
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role === "assistant" && lastMessage.content) {
      if (!isWindowOpen || !isPageVisible) {
        try {
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => { });
          }
        } catch (e) { }
      }
    }
  }, [messages, isStreaming, isWindowOpen, isPageVisible]);

  const sendMessage = useCallback(
    (messageText, imageData = null) => {
      if (!socketRef.current || !isConnected) {
        console.error("Socket no conectado");
        return;
      }
      const userId = getUserId();
      socketRef.current.emit("chat_message", {
        user_id: userId,
        message: messageText,
        room_id: roomId,
        is_chat_bot: false,
        is_chat_ai: true,
        ...(imageData && { image: imageData }),
      });
      const userMessage = {
        role: "user",
        content: messageText,
        _id: `local_${Date.now()}_${Math.random()}`,
        image: imageData?.preview,
        createdAt: new Date().toISOString(),
        isLocal: true,
        is_chat_ai: true,
      };
      setMessages((prev) => [...prev, userMessage]);
    },
    [roomId, isConnected, getUserId]
  );

  const onSend = useCallback(async () => {
    const content = inputValue.trim();
    if ((!content && !pendingImage) || isStreaming || !isConnected) return;
    setIsStreaming(true);
    try {
      sendMessage(content, pendingImage);
      setInputValue("");
      setPendingImage(null);
      setIsStreaming(true);
    } catch (err) {
      setIsStreaming(false);
      console.error("Error enviando mensaje:", err);
      setMessages((prev) => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        if (lastIndex >= 0 && updated[lastIndex].role === "assistant") {
          updated[lastIndex] = {
            ...updated[lastIndex],
            content: "Sorry, there was a problem getting the response. Please try again.",
          };
        }
        return updated;
      });
    }
  }, [inputValue, isStreaming, isConnected, pendingImage, sendMessage]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }, [onSend]);

  const clearConversation = () => {
    if (window.confirm("Are you sure you want to clear the conversation?")) {
      setMessages([]);
      setPendingImage(null);
      const newRoomId = `room_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      setRoomId(newRoomId);
      if (socketRef.current && isConnected) {
        socketRef.current.emit("join_room", newRoomId);
      }
    }
  };

  const handlePickImage = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !file.type.startsWith("image/") || file.size > 4 * 1024 * 1024) return;
    const toDataUrl = (f) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(f);
    });
    const dataUrl = await toDataUrl(file);
    setPendingImage({ name: file.name, type: file.type, size: file.size, dataUrl, preview: dataUrl });
    e.target.value = "";
  };

  const handleOpenForm = (viewTitle) => {
    setFormTitle(viewTitle);
    setActiveView('form');
    setWindowOpen(true);
  };

  const handleOpenChat = () => {
    setActiveView('chat');
    setFormTitle('Message Us')
    setWindowOpen(true);
  };

  return (
    <>
      {/* Backdrop Overlay for Desktop */}
      {isWindowOpen && isCompanyInfoVisible && (
        <div
          onClick={handleCloseWindow}
          className="fixed inset-0 z-40 hidden bg-black/40 transition-opacity duration-300 ease-in-out md:block"
          aria-hidden="true"
        ></div>
      )}

      {/* Contact Bar */}
      <div className={`fixed h-[60px] md:w-[400px] w-full bottom-0 left-0 right-0 z-40 md:left-auto md:right-5 rounded-t-2xl md:rounded-t-lg overflow-hidden transition-all duration-500 ease-in-out ${!isWindowOpen ? 'block' : 'hidden md:block'} ${isBarVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}>
        <div className={`grid grid-cols-4 w-full h-full text-white ${isEmbedded ? '' : 'shadow-lg'}`}>
          <button onClick={() => handleOpenForm('Send us a text')} className="flex flex-col h-full bg-blue-600 mr-[1px] items-center justify-center p-3 text-center transition-colors hover:bg-blue-700 md:flex-row md:gap-2 md:px-4 md:py-3">
            <MessageSquare size={25} className="md:size-5" />
            <span className="mt-1 text-xs font-medium md:mt-0 md:text-lg">Text</span>
          </button>
          <button onClick={() => handleOpenForm('Request a callback')} className="flex flex-col h-full bg-blue-600 mr-[1px] items-center justify-center p-3 text-center transition-colors hover:bg-blue-700 md:flex-row md:gap-2 md:px-4 md:py-3">
            <Phone size={25} className="md:size-5" />
            <span className="mt-1 text-xs font-medium md:mt-0 md:text-lg">Call</span>
          </button>
          <button onClick={() => handleOpenForm('Send us an email')} className="flex flex-col h-full bg-blue-600 mr-[1px] items-center justify-center p-3 text-center transition-colors hover:bg-blue-700 md:flex-row md:gap-2 md:px-4 md:py-3">
            <Mail size={25} className="md:size-5" />
            <span className="mt-1 text-xs font-medium md:mt-0 md:text-lg">Email</span>
          </button>
          <button onClick={handleOpenChat} className="relative flex flex-col items-center h-full bg-blue-600 justify-center p-3 text-center transition-colors hover:bg-blue-700 md:flex-row md:gap-2 md:px-4 md:py-3">
            <MessageCircleMore size={25} className="md:size-5" />
            <span className="mt-1 text-xs font-medium md:mt-0 md:text-lg">Chat</span>
            <div className={`absolute top-2 right-2 h-2.5 w-2.5 rounded-full border-2 border-blue-600 md:top-2.5 md:right-3 ${isConnected ? "bg-green-400" : "bg-red-500"}`}></div>
          </button>
        </div>
      </div>

      {/* Widget Window */}
      <div className={`fixed inset-0 z-50 flex flex-col bg-white transition-all duration-300 ease-in-out md:inset-auto md:h-[70vh] md:max-h-[600px] md:bottom-24 md:right-5 md:rounded-xl ${isEmbedded ? '' : 'md:shadow-2xl'} ${isWindowOpen ? 'translate-y-0 md:opacity-100 md:visible' : 'translate-y-full md:translate-y-0 md:opacity-0 md:invisible'} ${isCompanyInfoVisible ? 'md:w-[700px]' : 'md:w-[400px]'}`}>
        <div className="flex items-center justify-between bg-blue-600 px-4 py-3 text-white flex-shrink-0">
          <div className="font-semibold">{activeView === 'chat' ? 'Message Us' : formTitle}</div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setCompanyInfoVisible(!isCompanyInfoVisible)} className="text-white/90 hover:text-white hidden md:block" aria-label="Toggle company info">
              {isCompanyInfoVisible ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </button>
            {activeView === 'chat' && (
              <button type="button" onClick={clearConversation} className="text-white/90 hover:text-white" aria-label="Clear conversation" title="Clear conversation">
                <Trash2 className="h-5 w-5" />
              </button>
            )}
            <button type="button" onClick={handleCloseWindow} className="text-white/90 hover:text-white" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 flex md:flex-row min-h-0">
          {/* Company Info Panel (Desktop Only & Animated) */}
          <div className={`hidden md:flex flex-col bg-white overflow-hidden transition-all duration-300 ease-in-out ${isCompanyInfoVisible ? 'w-2/5 p-4 border-r' : 'w-0'}`}>
            <div className="flex-shrink-0 p-2 text-center">
              <div className={`inline-block bg-white rounded-lg p-2 ${isEmbedded ? '' : 'shadow-md'}`}>
                <img src={globalVariable.logo} alt="Azucar Multimedia" className="h-12" />
              </div>
            </div>
            <div className="text-center">
              <h3 className="font-bold text-gray-800 text-lg">Azucar Multimedia</h3>
            </div>
            <div className="mt-4 divide-y">
              <div className="py-3 space-y-3 text-sm text-gray-600">
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="mt-0.5 text-gray-400 flex-shrink-0" />
                  <span>1960 Lafayette St, Santa Clara, CA 95050</span>
                </div>
                <a href="tel:+1-408-509-0579" className="flex items-center gap-3 hover:text-blue-600">
                  <Phone size={16} className="text-gray-400 flex-shrink-0" />
                  <span>+1-408-509-0579</span>
                </a>
              </div>
              <div className="py-3">
                <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2"><Clock size={16} /> Business Hours</h4>
                <div className="space-y-1 text-sm text-gray-600 pt-1 pl-1">
                  <div className="flex justify-between"><span>Monday - Friday</span> <span>9am - 5pm</span></div>
                  <div className="flex justify-between"><span>Saturday - Sunday</span> <span>Closed</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content (Chat/Form) */}
          <div className="flex-1 flex flex-col min-h-0">
            {activeView === 'chat' ? (
              <div className="flex-1 flex flex-col bg-gray-50 min-h-0">
                <div ref={listRef} className="flex-grow space-y-3 overflow-y-auto px-3 py-2">
                  {messages.length === 0 && (
                    <>
                      <div className="pt-2 text-center text-xs text-gray-400">This is the beginning of your conversation.</div>
                      <div className="px-2 text-sm text-gray-600">Thank you for contacting AzucarAI. By continuing you agree to our privacy policy.</div>
                      {showQuickOptions && (
                        <div className="flex flex-wrap gap-2 px-2">
                          <button onClick={() => handleOpenForm('Request a callback')} className="flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs hover:bg-gray-100" disabled={!isConnected}><Phone size={14} /> Request a Callback</button>
                          <button onClick={() => handleOpenForm('Send us a text')} className="flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs hover:bg-gray-100" disabled={!isConnected}><MessageSquare size={14} /> Text with Us</button>
                          <button onClick={() => handleOpenForm('Send us an email')} className="flex items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs hover:bg-gray-100" disabled={!isConnected}><Mail size={14} /> Email Us</button>
                        </div>
                      )}
                    </>
                  )}
                  {messages.map((m, idx) => (
                    <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "rounded-br-md bg-blue-600 text-white" : `rounded-bl-md bg-white text-gray-900 ${isEmbedded ? '' : 'shadow-sm'}`}`}>
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                        {m.image && <div className="mt-2"><img src={m.image} alt="attachment" className="max-h-40 rounded-lg" /></div>}
                        {m.isLocal && <div className="mt-1 text-xs opacity-50">Sending...</div>}
                      </div>
                    </div>
                  ))}
                  {isStreaming && (
                    <div className="flex justify-start">
                      <div className={`flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-xs text-gray-500 ${isEmbedded ? '' : 'shadow-sm'}`}>
                        <span>AzucarAI is replying...</span>
                        <span className="inline-flex">
                          <span className="mr-1 h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]"></span>
                          <span className="mr-1 h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]"></span>
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400"></span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="border-t border-gray-200 p-2 bg-white flex-shrink-0">
                  {pendingImage && (
                    <div className="mx-1 mb-2 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-2">
                      <div className="h-12 w-12 overflow-hidden rounded border border-gray-200 bg-white"><img src={pendingImage.preview} alt={pendingImage.name} className="h-full w-full object-cover" /></div>
                      <div className="min-w-0 flex-1"><div className="truncate text-xs font-medium">{pendingImage.name}</div><div className="text-[10px] text-gray-500">{formatBytes(pendingImage.size)}</div></div>
                      <button type="button" className="text-gray-500 hover:text-gray-700" aria-label="Remove image" onClick={() => setPendingImage(null)}><X className="h-4 w-4" /></button>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea rows={1} value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={handleKeyDown} className="max-h-28 flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={isConnected ? "Type your message..." : "Connecting..."} disabled={!isConnected} />
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                    <button type="button" onClick={handlePickImage} className="rounded-lg border border-gray-300 px-2 py-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50" title="Attach image" aria-label="Attach image" disabled={!isConnected}><ImagePlus className="h-5 w-5" /></button>
                    <button type="button" onClick={onSend} className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50" disabled={(!inputValue.trim() && !pendingImage) || !isConnected || isStreaming}>Send</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col bg-white min-h-0">
                <div className="flex-grow overflow-y-auto p-6">
                  <h2 className="text-center text-2xl font-bold text-gray-800">{formTitle}</h2>
                  <p className="mb-6 mt-2 text-center text-sm text-gray-500">
                    Let us know what you need, and we'll get back to you as soon as we can.
                  </p>
                  <FormAzucarai
                    is_in_modal={true}
                    form_title={formTitle}
                    set_request_contact_is_send={set_request_contact_is_send}
                  />
                </div>
                <div className="border-t border-gray-200 bg-gray-50 p-4 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleCloseWindow}
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                  >
                    <ArrowLeft className="inline h-4 w-4" /> Back
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BlueChatWidget;
